import React, { useEffect, useState, useRef, ReactNode, ReactElement } from 'react';
import {renderToStaticMarkup} from 'react-dom/server'
import _, { isBuffer } from 'lodash';
import { Point, ControlPointType } from '@pvplevelup/core/dist/interfaces/maps/IMaps';
import { IconButton } from '@material-ui/core';
import { Launch, ArrowBack, ArrowForward } from '@material-ui/icons';
import {colors} from '@material-ui/core'


export enum MapDataTypes {
  Circle = "Circle",
  Poly = "Poly",
  Image = "Image",
  Svg = "Svg"
}

export interface MapData {
  type: MapDataTypes;
  radius?: number;
  data?: [number,number] | Array<[number,number]>;
  image?: string;
  svg?: any;
  fillColor?: string;
  lineColor?: string | null;
  lineWidth?: number | null;
  alpha?: number;
  dWidth?: number;
  dHeight?: number;
  showBig?: boolean;
  radians?: number | null;
}

export interface PointData {
  point: Point;
  fillColor?: string;
  lineColor?: string | null;
  lineWidth?: number | null;
  showBig?: boolean;
  alpha?: number;
}

interface Props {
  src: string;
  width: number;
  data: Array<MapData>;
  pointData: Array<PointData>;
  xStart: number;
  yStart: number;
  xDistance: number;
  yDistance: number;
  isResizing: boolean;
  zoneSrc?: string;
  zoneXStart?: number;
  zoneYStart?: number;
  zoneXDistance?: number;
  zoneYDistance?: number;
}

function drawArrowhead(context:CanvasRenderingContext2D, from:{x:number,y:number}, to:{x:number,y:number}, radius:number, lineColor: string) {
	var x_center = to.x;
	var y_center = to.y;

	var angle;
	var x;
	var y;

  context.beginPath();

	angle = Math.atan2(to.y - from.y, to.x - from.x)
	x = radius * Math.cos(angle) + x_center;
	y = radius * Math.sin(angle) + y_center;

	context.moveTo(x, y);

	angle += (1.0/3.0) * (2 * Math.PI)
	x = radius * Math.cos(angle) + x_center;
	y = radius * Math.sin(angle) + y_center;

	context.lineTo(x, y);

	angle += (1.0/3.0) * (2 * Math.PI)
	x = radius *Math.cos(angle) + x_center;
	y = radius *Math.sin(angle) + y_center;

	context.lineTo(x, y);

	context.closePath();

  context.fill();
  context.lineWidth = 1;
  context.strokeStyle = lineColor;
  context.stroke();
}

function getArea(points: Array<[number, number]>) {
  var area = 0,
      i,
      j,
      point1,
      point2;

  for (i = 0, j = points.length - 1; i < points.length; j=i,i++) {
      point1 = points[i];
      point2 = points[j];
      area += point1[0] * point2[1];
      area -= point1[1] * point2[0];
  }
  area /= 2;

  return area;
}

function getPolygonCenter (points: Array<[number, number]>) {
  var x = 0,
      y = 0,
      i,
      j,
      f,
      point1,
      point2;

  for (i = 0, j = points.length - 1; i < points.length; j=i,i++) {
      point1 = points[i];
      point2 = points[j];
      f = point1[0] * point2[1] - point2[0] * point1[1];
      x += (point1[0] + point2[0]) * f;
      y += (point1[1] + point2[1]) * f;
  }

  f = getArea(points) * 6;

  return [x / f, y / f];
}

function deepCompareEquals(a:any, b:any){
  // TODO: implement deep comparison here
  // something like lodash
  return _.isEqual(a, b);
}

function useDeepCompareMemoize(value:any) {
  const ref = useRef() 
  // it can be done by using useMemo as well
  // but useRef is rather cleaner and easier

  if (!deepCompareEquals(value, ref.current)) {
    ref.current = value
  }

  return ref.current
}

export function useDeepCompareEffect(callback:any, dependencies:any) {
  useEffect(callback, useDeepCompareMemoize(dependencies))
}

const MapRenderer: React.FC<Props> = (props) => {
  let [data, setData] = useState<Array<MapData>>([]);
  let [points, setPoints] = useState<Array<PointData>>([]);
  let [height, setHeight] = useState(5);
  let [width, setWidth] = useState(5);
  let [originalWidth, setOriginalWidth] = useState(5);
  let [originalHeight, setOriginalHeight] = useState(5);
  const dataCanvasRef = React.useRef<HTMLCanvasElement | null>(null)
  const pointCanvasRef = React.useRef<HTMLCanvasElement | null>(null)
  const imagesRef = React.useRef<HTMLCanvasElement | null>(null)
  const [useZone, setUseZone] = useState(false);

  const getWidthHeight = (callback = ()=>{}) => {
    let image = new Image();
    image.src = useZone && props.zoneSrc ? props.zoneSrc : props.src;
    image.onload = () => {
      var ratio = Math.min(props.width / image.width, props.width / image.height);
      let width = image.width * ratio, height = image.height * ratio;
      setWidth(width || 5);
      setHeight(height || 5);
    };
  }

  const transformData = () => {
    let { data } = props;

    let xStart = useZone && props.zoneSrc && props.zoneXStart != null ? props.zoneXStart : props.xStart;
    let yStart = useZone && props.zoneSrc && props.zoneYStart != null ? props.zoneYStart : props.yStart;
    let xDistance = useZone && props.zoneSrc && props.zoneXDistance ? props.zoneXDistance : props.xDistance;
    let yDistance = useZone && props.zoneSrc && props.zoneYDistance ? props.zoneYDistance : props.yDistance;

    let adjustedWidthRatio = originalWidth / xDistance;
    let adjustedHeightRatio = originalHeight / yDistance;
      
    let widthRatio = width / originalWidth;
    let heightRatio = height / originalHeight;

    let mutatedData = data.map((d)=>{
      let radius = useZone && d.showBig ? (d.radius || 0) * 2 : d.radius || 0;
      let preUpdatedRadius = radius * adjustedWidthRatio;
      let updatedRadius = preUpdatedRadius * widthRatio;
      let updatedData: [number,number] | Array<[number,number]> = [];
      if((d.type == MapDataTypes.Circle || d.type == MapDataTypes.Image || d.type == MapDataTypes.Svg) && d.data != null){
        let adjustedX = (d.data[0] as number - xStart) * adjustedWidthRatio;
        let adjustedY = (d.data[1] as number - yStart) * adjustedHeightRatio;
        let x = adjustedX * widthRatio;
        let y = (originalHeight - adjustedY) * heightRatio;
        updatedData = [x,y];
      }else if(d.type == MapDataTypes.Poly && d.data != null){
        let arrData: Array<[number,number]> = d.data as Array<[number,number]>;
        updatedData = arrData.map((coord)=>{
          let adjustedX = (coord[0] as number - xStart) * adjustedWidthRatio;
          let adjustedY = (coord[1] as number - yStart) * adjustedHeightRatio;
          let x = adjustedX * widthRatio;
          let y = (originalHeight - adjustedY) * heightRatio;
          return [x,y] as [number,number];
        });
      }
      return {...d, data:updatedData, radius: updatedRadius};
    });
    return mutatedData;
  }

  const transformPoints = () => {
    let { pointData } = props;

    let xStart = useZone && props.zoneSrc && props.zoneXStart != null ? props.zoneXStart : props.xStart;
    let yStart = useZone && props.zoneSrc && props.zoneYStart != null ? props.zoneYStart : props.yStart;
    let xDistance = useZone && props.zoneSrc && props.zoneXDistance ? props.zoneXDistance : props.xDistance;
    let yDistance = useZone && props.zoneSrc && props.zoneYDistance ? props.zoneYDistance : props.yDistance;

    let adjustedWidthRatio = originalWidth / xDistance;
    let adjustedHeightRatio = originalHeight / yDistance;
      
    let widthRatio = width / originalWidth;
    let heightRatio = height / originalHeight;

    let mutatedData = pointData.map((d)=>{
      let radius = useZone && d.showBig ? (d.point.radius || 0) * 2 : d.point.radius || 0;
      let preUpdatedRadius = radius * adjustedWidthRatio;
      let updatedRadius = preUpdatedRadius * widthRatio;
      let updatedEpicenter: [number, number] | null = null;
      let updatedCoordinates: Array<[number, number]> | null = null;
      if((d.point.type == ControlPointType.Circle) && d.point.epicenter != null){
        let adjustedX = (d.point.epicenter[0] as number - xStart) * adjustedWidthRatio;
        let adjustedY = (d.point.epicenter[1] as number - yStart) * adjustedHeightRatio;
        let x = adjustedX * widthRatio;
        let y = (originalHeight - adjustedY) * heightRatio;
        updatedEpicenter = [x,y];
      }else if(d.point.type == ControlPointType.Poly && d.point.coordinates != null){
        updatedCoordinates = d.point.coordinates.map((coord)=>{
          let adjustedX = (coord[0] as number - xStart) * adjustedWidthRatio;
          let adjustedY = (coord[1] as number - yStart) * adjustedHeightRatio;
          let x = adjustedX * widthRatio;
          let y = (originalHeight - adjustedY) * heightRatio;
          return [x,y] as [number,number];
        });
      }
      let newData = {
        ...d,
        point:{
          ...d.point
        }
      }
      if(updatedEpicenter)
        newData.point.epicenter = updatedEpicenter;
      if(updatedCoordinates)
        newData.point.coordinates = updatedCoordinates;
      if(updatedRadius)
        newData.point.radius = updatedRadius;
      return newData;
    });
    return mutatedData;
  }

  const drawData = () => {
    const imageContext = imagesRef.current?.getContext('2d');
    const canvas = dataCanvasRef.current;
    /* eslint-disable prefer-const */
    let imageCtx = canvas?.getContext('2d');

    let dataCanvas = document.createElement('canvas');
    dataCanvas.width = width;
    dataCanvas.height = height;
    let dataCtx = dataCanvas.getContext('2d');
    /* eslint-enable prefer-const */

    imageCtx?.clearRect(0,0,width,height);
    imageContext?.clearRect(0,0,width,height);
    dataCtx?.clearRect(0,0,width,height);
    if(dataCtx != null){
      for (let i = 0, len = data.length, p; i < len; i++) {
        let p = data[i];
        if(dataCtx != null){
          if(p.type == MapDataTypes.Poly || p.type == MapDataTypes.Circle){
            dataCtx.fillStyle = p.fillColor || "blue";
            dataCtx.beginPath();
            dataCtx.globalAlpha = p.alpha || 1;
          }
          
          if(p.type == MapDataTypes.Poly){
            let d = p.data as Array<[number,number]>;
            for(let k = 0, klen = d.length, z; k < klen; k++){
              let z = d[k];
              if(k == 0){
                dataCtx.moveTo(z[0], z[1]);
              }else{
                dataCtx.lineTo(z[0], z[1]);
              }
            }
          }else if(p.type == MapDataTypes.Circle){
            let d = p.data as [number,number];
            dataCtx.arc(d[0], d[1], p.radius || 1, 0, 2 * Math.PI);
            
          }else if(p.type == MapDataTypes.Image){
            if(imageContext)
              imageContext.globalAlpha = p.alpha || 1;
            let d = p.data as [number,number];
            let nimage = new Image();
            nimage.onload = () => {
              nimage.style.zIndex = '20';
              if(p.dWidth && p.dHeight){
                imageContext?.drawImage(nimage, d[0] - (p.dWidth / 2), d[1] - (p.dHeight / 2),p.dWidth,p.dHeight);
              }else{
                imageContext?.drawImage(nimage, d[0] - (nimage.width / 2), d[1] - (nimage.height / 2));
              }
            }
            nimage.src = p.image || "";
          }
          
          if(p.type == MapDataTypes.Poly || p.type == MapDataTypes.Circle){
            dataCtx.closePath();
            dataCtx.fill();
            if(p.lineColor){
              dataCtx.lineWidth = p.lineWidth || 4;
              dataCtx.strokeStyle = p.lineColor;
              dataCtx.stroke();
            }
            if(p.type == MapDataTypes.Circle && p.radians){
              let d = p.data as [number,number];
              let radians = p.radians;
              radians = radians + Math.PI / 2;
              radians *= -1;
              let x = d[0] + (((p.radius || 0) + 5) * Math.cos(radians));
              let y = d[1] + (((p.radius || 0) + 5) * Math.sin(radians));

              let to = {x:x,y:y}
              drawArrowhead(dataCtx,{x:d[0], y:d[1]},to,useZone ? 5 : 8,p.lineColor || "black");
            }
          }
          
        }
      }

      imageCtx?.drawImage(dataCanvas,0,0);
    }
  }

  const drawPoints = () => {
    const canvas = pointCanvasRef.current;
    /* eslint-disable prefer-const */
    let imageCtx = canvas?.getContext('2d');

    let dataCanvas = document.createElement('canvas');
    dataCanvas.width = width;
    dataCanvas.height = height;
    let dataCtx = dataCanvas.getContext('2d');
    /* eslint-enable prefer-const */

    imageCtx?.clearRect(0,0,width,height);
    dataCtx?.clearRect(0,0,width,height);
    if(dataCtx != null){
      for (let i = 0, len = points.length, p; i < len; i++) {
        let p = points[i];
        if(dataCtx != null){
          dataCtx.fillStyle = p.fillColor || "blue";
          dataCtx.beginPath();
          dataCtx.globalAlpha = p.alpha || 1;
          
          if(p.point.type == ControlPointType.Poly){
            if(p.point.coordinates){
              for(let k = 0, klen = p.point.coordinates.length, z; k < klen; k++){
                let z = p.point.coordinates[k];
                if(k == 0){
                  dataCtx.moveTo(z[0], z[1]);
                }else{
                  dataCtx.lineTo(z[0], z[1]);
                }
              }
            }
          }else{
            if(p.point.epicenter){
              dataCtx.arc(p.point.epicenter[0], p.point.epicenter[1], p.point.radius || 1, 0, 2 * Math.PI);
            }
          }

          dataCtx.closePath();
          dataCtx.fill();
          if(p.lineColor){
            dataCtx.lineWidth = p.lineWidth || 4;
            dataCtx.strokeStyle = p.lineColor;
            dataCtx.stroke();
          }
        }
      }

      imageCtx?.drawImage(dataCanvas,0,0);
    }
  }
    

  useEffect(()=>{
    if(!props.isResizing)
      getWidthHeight();
    //drawImage();
  },[props.width, props.src, props.zoneSrc, useZone, props.isResizing]);

  useDeepCompareEffect(()=>{
    if(!props.isResizing){
      let newData = transformData();
      setData(newData);
    }
  },[width,height,props.data,props.isResizing,useZone,props.xStart, props.yStart, props.xDistance, props.yDistance])

  useDeepCompareEffect(()=>{
    if(!props.isResizing){
      let newPoints = transformPoints();
      setPoints(newPoints);
    }
  },[width,height,props.pointData,props.isResizing,useZone,props.xStart, props.yStart, props.xDistance, props.yDistance])

  useDeepCompareEffect(()=>{
    if(!props.isResizing)
      drawData();
  },[data,props.isResizing]);

  useDeepCompareEffect(()=>{
    if(!props.isResizing)
      drawPoints();
  },[points,props.isResizing])

  useEffect(()=>{
    let image = new Image();
    image.src = useZone && props.zoneSrc ? props.zoneSrc : props.src;
    image.onload = () => {
      setOriginalHeight(image.height);
      setOriginalWidth(image.width);
    };
  },[useZone])

  useEffect(()=>{
    if(props.src == ""){
      setUseZone(true);
    }else{
      setUseZone(false);
    }
  },[props.src])

  useEffect(()=>{
    let image = new Image();
    image.src = props.src;
    image.onload = () => {
      setOriginalHeight(image.height);
      setOriginalWidth(image.width);
    };
  },[])

  return <div style={{position: 'relative'}}>
    {!props.isResizing ?
    <React.Fragment>
      {(props.zoneSrc && props.src) &&
        <div className="nodrag" style={{position:'absolute',top:0,left:0,zIndex:100}}>
          <IconButton style={{color:colors.deepPurple[800]}}  aria-label="back" component="span" onClick={()=>{setUseZone(!useZone)}}>
            {useZone ? <ArrowForward /> : <ArrowBack />}
          </IconButton>
        </div>
      }
      <img src={useZone && props.zoneSrc ? props.zoneSrc : props.src} width={props.width} />
      <canvas style={{position:'absolute',left:0,top:0,zIndex:2}} ref={dataCanvasRef} width={width} height={height} />
      <canvas style={{position:'absolute',left:0,top:0,zIndex:1}} ref={pointCanvasRef} width={width} height={height} />
      <canvas style={{position:'absolute',left:0,top:0,zIndex:3}} ref={imagesRef} width={width} height={height} />
    </React.Fragment>
    :
    <div style={{width:props.width,height:props.width,border:'2px solid white'}}>
    </div>}
  </div>
}

export default MapRenderer;